import { messaging } from '@/feature_flags/firebase_config'

/** Served as-is from public/. It shows pushes and handles their clicks. */
export const PUSH_SERVICE_WORKER_URL = '/firebase-messaging-sw.js'

/**
 * useLocalStorage key for the last token registered from this browser.
 * Signing out reads it to remove this browser's device rows.
 */
export const PUSH_TOKEN_STORAGE_KEY = 'push-device-token'

/**
 * The Firebase messaging instance, or null when this browser can't do push
 * or no VAPID key is set. The key is public; a missing one turns push off.
 */
export async function getPushMessaging() {
  if (!import.meta.env.VITE_FIREBASE_VAPID_KEY) return null
  if (!('Notification' in window) || !('serviceWorker' in navigator)) {
    return null
  }
  return messaging()
}

/**
 * This browser's FCM token. Only call it once notification permission is
 * granted, or the browser shows its own permission dialog.
 */
export async function getPushToken() {
  const messagingInstance = await getPushMessaging()
  if (!messagingInstance) return null

  // Registered at the root scope rather than letting Firebase register it
  // under its own path, so the worker can take control of app tabs and
  // navigate one when a push is clicked.
  await navigator.serviceWorker.register(PUSH_SERVICE_WORKER_URL)
  // Firebase only waits for a worker it registers itself. On the first visit
  // ours is still installing here, and getToken fails with "no active Service
  // Worker" unless this waits for it.
  const serviceWorkerRegistration = await navigator.serviceWorker.ready
  // Imported here, not at the top of the file, for the same reason as in
  // firebase_config.js: a static import puts the SDK in the boot bundle.
  const { getToken } = await import('@firebase/messaging')
  return getToken(messagingInstance, {
    vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
    serviceWorkerRegistration,
  })
}

const SIGN_OUT_WAIT_MS = 5_000

function withTimeout<T>(promise: Promise<T>, fallback: T) {
  return Promise.race([
    promise,
    new Promise<T>((resolve) => {
      setTimeout(() => resolve(fallback), SIGN_OUT_WAIT_MS)
    }),
  ])
}

let pendingRegistration: Promise<string | null> | undefined

/**
 * Remembers a registration while it's in flight, so signing out can wait for
 * it. Otherwise the registration could recreate the device row just after
 * sign-out deleted it. Resolves with the token it registered.
 */
export function trackPushRegistration(registration: Promise<string | null>) {
  pendingRegistration = registration
  const clear = () => {
    if (pendingRegistration === registration) pendingRegistration = undefined
  }
  registration.then(clear, clear)
  return registration
}

/**
 * The token from a registration still in flight, once it lands, or null when
 * there is none. Gives up after a few seconds so sign-out can't hang on it.
 */
export async function waitForPushRegistration() {
  if (!pendingRegistration) return null
  try {
    return await withTimeout(pendingRegistration, null)
  } catch {
    return null
  }
}

/**
 * Deletes this browser's FCM token, so FCM rejects anything still sent to it
 * and pd-notify marks the device invalid. Sign-out uses it when removing the
 * device row fails. Returns whether the token was deleted.
 */
export async function revokePushToken() {
  const messagingInstance = await getPushMessaging()
  if (!messagingInstance || Notification.permission !== 'granted') {
    return false
  }
  // Getting the token first points Firebase at our worker. Without a worker
  // already set, deleteToken registers Firebase's own under another scope.
  await getPushToken()
  const { deleteToken } = await import('@firebase/messaging')
  return withTimeout(deleteToken(messagingInstance), false)
}
